"use client"

import { type ComponentProps, type HTMLAttributes, type Ref } from "react"
import { Select as SelectPrimitive } from "@base-ui/react/select"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "cn"

import IconCheck from "@/components/icons/check"
import IconChevronDown from "@/components/icons/chevron-down"
import IconChevronUp from "@/components/icons/chevron-up"
import { Loading } from "./loading"
import { INPUT_VARIANT_SIZES } from "./text-input"

const selectTriggerVariants = cva(
  "peer relative flex w-full min-w-[4.25em] items-center justify-between gap-1.5 rounded-full border border-input bg-background text-foreground transition-colors hover:border-gray-8 focus:border-current focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-muted",
  {
    variants: {
      size: INPUT_VARIANT_SIZES,
      loading: {
        true: "pointer-events-none",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const Select = SelectPrimitive.Root

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1 p-1", className)}
      {...props}
    />
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("truncate text-left", className)}
      {...props}
    />
  )
}

interface SelectTriggerProps
  extends
    SelectPrimitive.Trigger.Props,
    VariantProps<typeof selectTriggerVariants> {
  loading?: boolean
}

function SelectTrigger({
  className,
  size,
  children,
  loading,
  render,
  ...props
}: SelectTriggerProps) {
  if (render) {
    return (
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        render={render}
        {...props}
      />
    )
  }
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(selectTriggerVariants({ size, loading }), className)}
      {...props}
    >
      <span className={cn("contents", loading && "invisible")}>
        {children}
        <SelectPrimitive.Icon
          render={
            <IconChevronDown className="-mr-0.5 stroke-2 text-[.875em]" />
          }
        />
      </span>
      {loading && (
        <span className="absolute top-1/2 left-1/2 -translate-1/2">
          <Loading size=".325em" />
        </span>
      )}
    </SelectPrimitive.Trigger>
  )
}

function SelectTriggerDisplayOnly({
  ref,
  className,
  children,
  size,
  ...props
}: HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof selectTriggerVariants> & {
    ref?: Ref<HTMLDivElement>
  }) {
  return (
    <div
      ref={ref}
      className={cn(
        "cursor-pointer select-none",
        selectTriggerVariants({ size }),
        className
      )}
      {...props}
    >
      <span className="truncate">{children}</span>
      <IconChevronDown className="-mr-0.5 stroke-2 text-[.875em]" />
    </div>
  )
}
SelectTriggerDisplayOnly.displayName = "SelectTriggerDisplayOnly"

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = true,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-overlay"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            "relative isolate z-overlay max-h-(--available-height) min-w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-xl bg-surface-2 px-2 py-2.5 leading-tight text-foreground shadow-ring-xl duration-100 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("py-1.5 pr-2 pl-8 text-xs font-semibold", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  description,
  ...props
}: SelectPrimitive.Item.Props & { description?: string }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex min-h-8 cursor-default rounded-lg py-[.442em] pr-2 pl-8 outline-hidden select-none focus:bg-foreground/5 data-disabled:pointer-events-none data-disabled:opacity-50",
        description ? "flex-col items-start" : "items-center",
        className
      )}
      {...props}
    >
      <span className="absolute top-[.55em] left-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <IconCheck size={13} strokeWidth={3} />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {description && <span className="text-xs text-muted">{description}</span>}
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(
        "pointer-events-none my-1.5 h-px bg-separator first:hidden last:hidden [&+&]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-background py-1",
        className
      )}
      {...props}
    >
      <IconChevronUp className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-background py-1",
        className
      )}
      {...props}
    >
      <IconChevronDown className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerDisplayOnly,
  SelectValue,
}
